import { formatCurrency, formatQuantity, formatRate, formatPercentage } from '@/utils/formatters';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Workflow, Plus, Play, Pause, Trash2, Edit, Eye, 
  Loader2, Zap, CheckCircle2, XCircle, Clock, ArrowRight, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SWSWorkflowsPage = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [deleting, setDeleting] = useState(null);
  
  // Instances dialog state
  const [instancesDialogOpen, setInstancesDialogOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [instances, setInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const [workflowsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/sws/workflows`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/sws/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (!workflowsRes.ok) throw new Error('Failed to fetch workflows');
      
      const workflowsData = await workflowsRes.json();
      const statsData = await statsRes.json();

      setWorkflows(workflowsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = async (workflowId, currentStatus) => {
    const token = localStorage.getItem('token');
    setToggling(workflowId);

    try {
      const res = await fetch(`${API}/api/sws/workflows/${workflowId}/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to toggle');
      
      toast.success(`Workflow ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      console.error('Error toggling:', error);
      toast.error('Failed to toggle workflow');
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async (workflowId, workflowName) => {
    if (!window.confirm(`Are you sure you want to delete "${workflowName}"? This cannot be undone.`)) {
      return;
    }

    const token = localStorage.getItem('token');
    setDeleting(workflowId);

    try {
      const res = await fetch(`${API}/api/sws/workflows/${workflowId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete');
      }
      toast.success('Workflow deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.message);
    } finally {
      setDeleting(null);
    }
  };

  const viewInstances = async (workflow) => {
    setSelectedWorkflow(workflow);
    setInstancesDialogOpen(true);
    setInstancesLoading(true);
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/sws/workflows/${workflow.id}/instances?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch instances');
      const data = await res.json();
      setInstances(data);
    } catch (error) {
      console.error('Error fetching instances:', error);
      toast.error('Failed to load workflow instances');
      setInstances([]);
    } finally {
      setInstancesLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getInstanceStatusBadge = (status) => {
    const styles = {
      running: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      failed: 'bg-red-100 text-red-800 border-red-300',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getStepsSummary = (steps) => {
    if (!steps || steps.length === 0) return 'No steps';
    
    const taskSteps = steps.filter(s => s.step_type === 'task');
    const conditionSteps = steps.filter(s => s.step_type === 'condition');
    const gotoSteps = steps.filter(s => s.step_type === 'goto');
    
    const parts = [];
    if (taskSteps.length > 0) parts.push(`${taskSteps.length} task(s)`);
    if (conditionSteps.length > 0) parts.push(`${conditionSteps.length} condition(s)`);
    if (gotoSteps.length > 0) parts.push(`${gotoSteps.length} loop(s)`);
    
    return parts.join(', ');
  };

  const getStepsPreview = (steps) => {
    if (!steps || steps.length === 0) return null;
    
    return steps.slice(0, 4).map((step, idx) => {
      if (step.step_type === 'task') {
        return (
          <span key={step.id} className="flex items-center gap-1 text-xs">
            {idx > 0 && <ArrowRight className="w-3 h-3 text-gray-400" />}
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
              {step.task_config?.title?.split(' - ')[0] || 'Task'}
            </span>
          </span>
        );
      } else if (step.step_type === 'condition') {
        return (
          <span key={step.id} className="flex items-center gap-1 text-xs">
            {idx > 0 && <ArrowRight className="w-3 h-3 text-gray-400" />}
            <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
              {step.name || step.condition_config?.name || 'Check'}
            </span>
          </span>
        );
      } else if (step.step_type === 'goto') {
        return (
          <span key={step.id} className="flex items-center gap-1 text-xs">
            {idx > 0 && <ArrowRight className="w-3 h-3 text-gray-400" />}
            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
              {step.name || 'Loop'}
            </span>
          </span>
        );
      }
      return null;
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="space-y-6" data-testid="sws-workflows-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Workflow className="w-6 h-6 text-purple-600" />
            Smart Workflow System (SWS)
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage automated workflows for task routing
          </p>
        </div>
        <Button 
          onClick={() => navigate('/admin/sws/builder')}
          className="gap-2"
          data-testid="create-workflow-btn"
        >
          <Plus className="w-4 h-4" />
          Create Workflow
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Workflows</p>
                  <p className="text-2xl font-bold">{stats.total_workflows}</p>
                </div>
                <Workflow className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active</p>
                  <p className="text-2xl font-bold text-green-600">{stats.active_workflows}</p>
                </div>
                <Play className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Running Instances</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.running_instances}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.completed_instances}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Workflows List */}
      <div className="space-y-4">
        <h2 className="text-lg font-medium text-gray-900">Workflows</h2>
        
        {workflows.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Workflow className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No workflows yet</h3>
              <p className="text-sm text-gray-500 mb-4">
                Create your first workflow to automate task routing
              </p>
              <Button onClick={() => navigate('/admin/sws/builder')}>
                <Plus className="w-4 h-4 mr-2" />
                Create Workflow
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {workflows.map(workflow => (
              <Card 
                key={workflow.id} 
                className={`border ${workflow.is_active ? 'border-gray-200' : 'border-gray-100 bg-gray-50'}`}
                data-testid={`workflow-card-${workflow.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900">{workflow.name}</h3>
                        <Badge variant={workflow.is_active ? "default" : "secondary"} className="text-xs">
                          {workflow.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      
                      {workflow.description && (
                        <p className="text-sm text-gray-500 mb-2">{workflow.description}</p>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Zap className="w-3.5 h-3.5" />
                          Trigger: <span className="font-medium text-gray-700">{workflow.trigger_event}</span>
                        </span>
                        <span>•</span>
                        <span>{getStepsSummary(workflow.steps)}</span>
                        <span>•</span>
                        <span>Executed: {workflow.execution_count || 0} times</span>
                      </div>
                      
                      <div className="flex items-center gap-1 flex-wrap">
                        {getStepsPreview(workflow.steps)}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <div className="flex items-center gap-2 mr-4">
                        <span className="text-xs text-gray-500">
                          {workflow.is_active ? 'On' : 'Off'}
                        </span>
                        <Switch
                          checked={workflow.is_active}
                          onCheckedChange={() => handleToggle(workflow.id, workflow.is_active)}
                          disabled={toggling === workflow.id}
                        />
                      </div>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/admin/sws/builder/${workflow.id}`)}
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => viewInstances(workflow)}
                        title="View Instances"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(workflow.id, workflow.name)}
                        disabled={deleting === workflow.id}
                        title="Delete"
                      >
                        {deleting === workflow.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* Instances Dialog */}
    <Dialog open={instancesDialogOpen} onOpenChange={setInstancesDialogOpen}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Workflow className="w-5 h-5" />
            {selectedWorkflow?.name} - Instances
          </DialogTitle>
          <DialogDescription>
            All workflow instances triggered by: {selectedWorkflow?.trigger_event}
          </DialogDescription>
        </DialogHeader>
        
        {instancesLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            <span>Loading instances...</span>
          </div>
        ) : instances.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Workflow className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No instances found for this workflow</p>
            <p className="text-sm mt-1">Instances are created when the trigger event occurs</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Entity</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[80px]">Step</TableHead>
                  <TableHead className="w-[150px]">Started</TableHead>
                  <TableHead className="w-[150px]">Completed</TableHead>
                  <TableHead>Trigger Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {instances.map((instance) => (
                  <TableRow key={instance.id}>
                    <TableCell className="font-mono text-xs">
                      {instance.trigger_data?.gate_entry_no || 
                       instance.trigger_data?.item_code_request_no ||
                       instance.trigger_data?.so_number ||
                       instance.entity_id?.substring(0, 8) || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${getInstanceStatusBadge(instance.status)}`}>
                        {instance.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {instance.current_step_sequence !== undefined ? 
                        `Step ${instance.current_step_sequence}` : '-'}
                    </TableCell>
                    <TableCell className="text-xs">{formatDateTime(instance.created_at)}</TableCell>
                    <TableCell className="text-xs">{formatDateTime(instance.completed_at)}</TableCell>
                    <TableCell className="text-xs max-w-[200px]">
                      <div className="truncate" title={JSON.stringify(instance.trigger_data)}>
                        {instance.trigger_data?.supplier_name || 
                         instance.trigger_data?.requested_by_name ||
                         instance.trigger_data?.client_name || '-'}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Showing {instances.length} instance(s)
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </DashboardLayout>
  );
};

export default SWSWorkflowsPage;
